import { useNavigate } from "react-router-dom";
import { LDSButton, LDSIconTextField } from "@resmed/lumina-design-system";
import Icons from "@resmed/lumina-design-system-assets/dist/icons";
import ConversationListItem from "../../../components/ConversationListItem";
import { InboxError, InboxNoData } from "../../../components/NoDataView";
import { SkeletonConversationList } from "../../../components/skeleton";
import { SectionLabel } from "../../../components/VirtualConversationList";
import type { Conversation, ChatWindowViewModelState } from "../ChatWindowViewModel";

// ── Tab definitions ──────────────────────────────────────────────────────────
const TABS = ["Teams", "Archived"] as const;

// ── Conversation list (mirrors MessagePanel messages scroll) ─────────────────
const ConversationList = ({
  conversations,
  selectedId,
  onSelect,
}: {
  conversations: Conversation[];
  selectedId: string;
  onSelect: (id: string) => void;
}) => {
  if (!conversations.length) return <InboxNoData />;

  const pinned = conversations.filter((c) => c.pinnedStatus);
  const recent = conversations.filter((c) => !c.pinnedStatus);

  const renderItem = (c: Conversation) => (
    <ConversationListItem
      key={c.id}
      name={c.name}
      subtitle={c.subtitle}
      time={c.time}
      showExternal={c.showExternal}
      avatarLabel={c.avatarLabel}
      icon={c.icon}
      pinnedStatus={c.pinnedStatus}
      unread={c.unread}
      isSelected={selectedId === c.id}
      onClick={() => onSelect(c.id)}
    />
  );

  return (
    <>
      {pinned.length > 0 && <SectionLabel label="Pinned" />}
      {pinned.map(renderItem)}
      {pinned.length > 0 && <SectionLabel label="Recent" />}
      {recent.map(renderItem)}
    </>
  );
};

interface ConversationSidebarProps {
  vm: ChatWindowViewModelState;
}

const ConversationSidebar = ({ vm }: ConversationSidebarProps) => {
  const navigate = useNavigate();
  const conversations =
    vm.activeTabIndex === 0 ? vm.teamConversations : vm.archivedConversations;

  return (
    <div className="lds-w-[40%] lds-shrink-0 lds-bg-white lds-flex lds-flex-col lds-min-h-0 lds-overflow-hidden">

      {/* Search bar — shrink-0 so it never scrolls away */}
      <div className="lds-flex lds-items-center lds-gap-2 lds-pr-3 lds-py-3 lds-shrink-0">
        <LDSIconTextField
          icon={Icons.FaMagnifyingGlass}
          iconPosition="left"
          placeholder="Search chat"
          size="medium"
          value={vm.searchQuery}
          onChange={(e) => vm.onSearchChange(e.target.value)}
          containerClassName="lds-flex-1"
        />
        <LDSButton
          id="new-chat-btn"
          icon={Icons.FaPlus}
          severity="default"
          size="medium"
          onClick={() => navigate("create-chat")}
        />
      </div>

      {/* Tab container — same structure as MessagePanel */}
      <div className="lds-flex lds-flex-col lds-flex-1 lds-min-h-0 lds-rounded-md lds-rounded-tr-none lds-border lds-border-bordercolor-weak">

        {/* Tab headers — shrink-0, exactly like ChatHeader in MessagePanel */}
        <div className="lds-flex lds-items-center lds-shrink-0 lds-px-4 lds-pt-3 lds-border-b lds-border-bordercolor-weak">
          {TABS.map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => vm.onTabChange(i)}
              className={[
                "lds-relative lds-flex lds-items-center lds-select-none",
                "lds-typography-base-medium lds-mx-1 lds-px-2 lds-py-2 lds-rounded-t",
                "lds-bg-transparent lds-cursor-pointer lds-border-0 lds-border-b-2",
                "lds-transition-colors lds-duration-200",
                vm.activeTabIndex === i
                  ? "lds-border-actions-accent lds-text-textcolor-default"
                  : "lds-border-transparent lds-text-textcolor-caption hover:lds-text-textcolor-default hover:lds-bg-list-row-hover hover:lds-rounded",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Scroll area — flex-1 overflow-y-auto, DIRECT flex child = works like MessagePanel */}
        <div className="lds-flex-1 lds-overflow-y-auto">
          {vm.inboxLoading ? (
            <SkeletonConversationList />
          ) : vm.inboxError ? (
            <InboxError onRetry={vm.onRetry} />
          ) : (
            <ConversationList
              conversations={conversations}
              selectedId={vm.selectedId}
              onSelect={vm.selectConversation}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationSidebar;

